import {
  Router, AuthRoutes,
} from '@equifinance/shared';
import {
  Routes, Route, Outlet, useOutletContext,
} from 'react-router-dom';

import { useState } from 'react';
import { ApplicationRoutes } from './ApplicationRoutes';
import { HomepageRoutes } from './HomepageRoutes';
import {
  TasksRoutes, ChecklistRoutes, RoutingContextType, defaultContextValue,
  AuditRoutes,
} from '.';

import Login from '../pages/Login';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import Reports from '../pages/Reports';
import Documents from '../pages/Documents';
import Decisioning from '../pages/Decisioning';
import Approved from '../pages/Approved';
import Commitments from '../pages/Commitments';
import DownloadESIS from '../pages/DownloadESIS';
import Calculator from '../pages/Calculator';
import Referrals from '../pages/Referrals';
import ReferralReasons from '../pages/ReferralReasons';

import CreditPage from '../components/Credit/CreditPage';
import AccountPage from '../components/Account/AccountPage';
import ProductsPage from '../components/Products/ProductsPage';
import BrokerUserPage from '../components/MyDetails/BrokerUserPage';
import { BrokerRoutes } from './BrokerRoutes';
import { ProductRoutes } from './ProductRoutes';
import { CreditRoutes } from './CreditRoutes';
import TasksPage from '../components/Tasks/TasksPage';
import ChecklistPage from '../components/Checklist/ChecklistPage';
import AuditPage from '../components/Audit/AuditPage';
import NotesPage from '../components/Notes/NotesPage';
import Forbidden from '../pages/Forbidden';
import { BrokerManagementRoutes } from './BrokerManagementRoutes';
import IncomeAndEmploymentInfo from '../pages/IncomeAndEmploymentInfo';
import MonthlyExpenditureHelp from '../pages/MonthlyExpenditureHelp';
import ManageUserPage from '../pages/ManageUser';
import { ManageUserRoutes } from './ManageUserRoutes';
import { CompanyRoutes } from './CompanyRoutes';
import BrokerDetails from '../pages/BrokerDetails';
import { BrokerDetailsRoutes } from './BrokerDetailsRoutes';

export function useRoutingContext() {
  return useOutletContext<RoutingContextType>();
}

function DefaultRoutes() {
  return <Outlet context={defaultContextValue} />;
}

function NavRoutes() {
  const [canOpenScreenSizeAlert, setCanOpenScreenSizeAlert] = useState(true);
  const outletValue: RoutingContextType = useOutletContext();
  return (
    <Outlet
      context={{
        ...outletValue,
        navRoute: true,
        canOpenScreenSizeAlert,
        setCanOpenScreenSizeAlert,
      }}
    />
  );
}

export function MainRoutes() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<DefaultRoutes />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/sign-up/setup-password" element={<ResetPassword isSetupPassword />} />

          <Route path="/" element={<AuthRoutes<RoutingContextType> loginPath="/login" />}>
            <Route path="/" element={<NavRoutes />}>
              <Route path="/" element={<HomepageRoutes />} />
              <Route path="/broker-management" element={<BrokerManagementRoutes />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/decisioning/:id" element={<Decisioning />} />
              <Route path="/approved/:id" element={<Approved />} />
              <Route path="/commitments/:id" element={<Commitments />} />
              <Route path="/download-esis/:id" element={<DownloadESIS />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/referral-reasons/:id" element={<ReferralReasons />} />
              <Route path="/help/monthly-expenditure/:version" element={<MonthlyExpenditureHelp />} />

              <Route path="/" element={<CompanyRoutes />}>
                <Route path="/" element={<BrokerDetailsRoutes />}>
                  <Route path="/broker-details/:id" element={<BrokerDetails />} />
                </Route>

                <Route path="/" element={<ManageUserRoutes />}>
                  <Route path="/manage-user/:id" element={<ManageUserPage />} />
                </Route>
              </Route>

              <Route path="/" element={<ApplicationRoutes />}>
                <Route path="/application/:id/income-employment-detail/:applicantId" element={<IncomeAndEmploymentInfo />} />
                <Route path="/" element={<CreditRoutes />}>
                  <Route path="/" element={<ProductRoutes />}>
                    <Route path="/products/:id" element={<ProductsPage />} />

                    <Route path="/credit/:id" element={<CreditPage />} />

                    <Route path="/application/:id" element={<AccountPage />} />
                    <Route path="/calculator" element={<Calculator />} />
                    <Route path="/" element={<TasksRoutes />}>
                      <Route path="/tasks/:id" element={<TasksPage />} />
                    </Route>
                    <Route path="/documents/:id" element={<Documents />} />
                    <Route path="/" element={<ChecklistRoutes />}>
                      <Route path="/checklist/:id" element={<ChecklistPage />} />
                    </Route>
                    <Route path="/" element={<AuditRoutes />}>
                      <Route path="/audit/:id" element={<AuditPage />} />
                    </Route>
                    <Route path="/notes/:id" element={<NotesPage />} />
                  </Route>
                </Route>
              </Route>

              <Route path="/" element={<BrokerRoutes />}>
                <Route path="/broker-user" element={<BrokerUserPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="/403" element={<Forbidden />} />
        </Route>
      </Routes>
    </Router>
  );
}
